import { ChangeEvent, useCallback, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
    dashboardActions,
    progressSelector,
} from '../../../../store/slices/dashboardSlice/dashboardSlice';
import { getNameExtension } from '../../../Files/helpers/getNameExtension';
import { isNotValidateSize } from '../../../Files/helpers/isNotValidateSize';
import {
    ProgressContextProps,
    UploadedProgressMediaItem,
    UploadMediaItem,
} from '../../Context/ProgressContext';

const maxCount = 10;
const validatedFormat: string[] = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'mp4',
    'mov',
    'avi',
    'mkv',
    'm4v',
    'wmv',
    'webm',
    'asf',
];
enum Errors {
    FORMAT = i18next.t('useConstructionProgressBlock.return.enumErrors.format'),
    SIZE = i18next.t('useConstructionProgressBlock.return.enumErrors.size'),
    COUNT = `${i18next.t(
        'useConstructionProgressBlock.return.enumErrors.count_1'
    )} ${maxCount} ${i18next.t('useConstructionProgressBlock.return.enumErrors.count_2')}`,
}

export const useConstructionProgressBlock = () => {
    const dispatch = useDispatch();

    const { t } = useTranslation();

    const mediaIds = useSelector(progressSelector.selectIds);

    const [isShowPopup, setIsShowPopup] = useState(false);
    const [isUploadQueueStarted, setUploadQueueStarted] = useState(false);
    const [isActiveMassSelectMode, setIsActiveMassSelectMode] = useState(false);
    const [selectedMassMediaIds, setSelectedMassMediaIds] = useState<string[]>([]);

    const activeMassSelectModeHandle = (value: boolean) => {
        if (!value && selectedMassMediaIds.length) {
            setSelectedMassMediaIds([]);
            return;
        }

        setSelectedMassMediaIds([]);
        setIsActiveMassSelectMode(value);
    };

    const onSelectedMassMediaIds = (id: string) => {
        setSelectedMassMediaIds((prevState) =>
            prevState.includes(id) ? prevState.filter((i) => i !== id) : [...prevState, id]
        );
    };
    const onALLSelectedMassMediaIds = () => {
        setSelectedMassMediaIds(mediaIds.map((id) => String(id)));
    };

    const { is_customer, progress_tariff_access_edit, is_owner, modalMode } = useSelector(
        // @ts-ignore
        (state) => state.dashboardReducer
    );

    const [uploadMediaList, setUploadMediaList] = useState<UploadMediaItem[]>([]);
    const [uploadedProgressMediaList, setUploadedProgressMediaList] = useState<
        UploadedProgressMediaItem[]
    >([]);
    const uploadImagesList = uploadMediaList.map((media) => media.file);
    const uploadMediaListRef = useRef<UploadMediaItem[]>([]);
    const pendingUploadFilesRef = useRef<File[]>([]);
    const uploadAbortControllersRef = useRef<Record<string, AbortController>>({});

    const clearUploadListImages = useCallback(() => {
        Object.values(uploadAbortControllersRef.current).forEach((controller) => {
            controller.abort();
        });
        uploadAbortControllersRef.current = {};
        uploadMediaListRef.current = [];
        pendingUploadFilesRef.current = [];
        setUploadQueueStarted(false);
        setUploadMediaList([]);
    }, []);

    const setUploadMediaUploadingState = useCallback((ids: string[], isUploading: boolean) => {
        uploadMediaListRef.current = uploadMediaListRef.current.map((item) =>
            ids.includes(item.id)
                ? {
                      ...item,
                      isUploading,
                  }
                : item
        );
        setUploadMediaList(uploadMediaListRef.current);
    }, []);

    const setUploadMediaError = useCallback((ids: string[], errorText: string) => {
        uploadMediaListRef.current = uploadMediaListRef.current.map((item) =>
            ids.includes(item.id)
                ? {
                      ...item,
                      errorText,
                  }
                : item
        );
        setUploadMediaList(uploadMediaListRef.current);
    }, []);

    const setUploadMediaMeta = useCallback((ids: string[], date: string, description: string) => {
        uploadMediaListRef.current = uploadMediaListRef.current.map((item) =>
            ids.includes(item.id)
                ? {
                      ...item,
                      date,
                      description,
                  }
                : item
        );
        setUploadMediaList(uploadMediaListRef.current);
    }, []);

    const removeUploadMediaItems = useCallback((ids: string[]) => {
        uploadMediaListRef.current = uploadMediaListRef.current.filter(
            (item) => !ids.includes(item.id)
        );
        setUploadMediaList(uploadMediaListRef.current);
    }, []);

    const setUploadAbortController = useCallback(
        (id: string, controller: AbortController | null) => {
            if (controller) {
                uploadAbortControllersRef.current[id] = controller;
                return;
            }

            delete uploadAbortControllersRef.current[id];
        },
        []
    );

    const abortUploadMediaItem = useCallback((id: string) => {
        const controller = uploadAbortControllersRef.current[id];
        if (controller) {
            controller.abort();
            delete uploadAbortControllersRef.current[id];
        }

        uploadMediaListRef.current = uploadMediaListRef.current.filter((item) => item.id !== id);
        setUploadMediaList(uploadMediaListRef.current);
    }, []);

    const getUploadMediaListSnapshot = useCallback(() => uploadMediaListRef.current, []);

    const appendPendingUploadMediaItems = useCallback(() => {
        const pendingFiles = pendingUploadFilesRef.current;
        if (!pendingFiles.length) {
            return [];
        }

        const uploadBatchId = Date.now();
        const newUploadItems = pendingFiles.map((file, index) => ({
            id: `${uploadBatchId}-${index}-${file.name}-${file.lastModified}`,
            file,
            isUploading: false,
            errorText: '',
            date: '',
            description: '',
        }));

        pendingUploadFilesRef.current = [];
        uploadMediaListRef.current = [...uploadMediaListRef.current, ...newUploadItems];
        setUploadMediaList(uploadMediaListRef.current);

        return newUploadItems.map((item) => item.id);
    }, []);

    const clearPendingUploadMediaItems = useCallback(() => {
        pendingUploadFilesRef.current = [];
    }, []);

    const addUploadedProgressMediaItems = useCallback((items: UploadedProgressMediaItem[]) => {
        setUploadedProgressMediaList((prevState) => [...items, ...prevState]);
    }, []);

    const clearUploadedProgressMediaList = useCallback(() => {
        setUploadedProgressMediaList([]);
    }, []);

    const onImageChange = (images: File[]) => {
        const uploadImageList: File[] = [];
        let overallSize = 0;
        const existingUploadCount = uploadMediaListRef.current.length;
        if (existingUploadCount + images.length > maxCount) {
            // @ts-ignore
            swal({
                text: Errors.COUNT,
                cancel: false,
                confirm: true,
                button: {
                    text: `${t('useConstructionProgressBlock.return.swal.button.text')}`,
                    value: true,
                    visible: true,
                    className: 'btn btn-secondary',
                },
            });
            return;
        }

        for (let i = 0; i < images.length; i++) {
            const { extension } = getNameExtension(images[i].name);
            if (!validatedFormat.includes(extension.toLowerCase())) {
                // @ts-ignore
                swal({
                    text: Errors.FORMAT,
                    cancel: false,
                    confirm: true,
                    button: {
                        text: `${t('useConstructionProgressBlock.return.swal.button.text')}`,
                        value: true,
                        visible: true,
                        className: 'btn btn-secondary',
                    },
                });
                return;
            }

            const checkValidate = isNotValidateSize(images[i].size, 1000);

            if (checkValidate) {
                // @ts-ignore
                swal({
                    text: Errors.SIZE,
                    cancel: false,
                    confirm: true,
                    button: {
                        text: `${t('useConstructionProgressBlock.return.swal.button.text')}`,
                        value: true,
                        visible: true,
                        className: 'btn btn-secondary',
                    },
                });
                return;
            }
            uploadImageList.push(images[i]);

            overallSize += images[i].size;
        }

        const isOverallSize = isNotValidateSize(overallSize, 1000);

        if (isOverallSize) {
            // @ts-ignore
            swal({
                text: Errors.SIZE,
                cancel: false,
                confirm: true,
                button: {
                    text: `${t('useConstructionProgressBlock.return.swal.button.text')}`,
                    value: true,
                    visible: true,
                    className: 'btn btn-secondary',
                },
            });
            return;
        }
        pendingUploadFilesRef.current = uploadImageList;
        dispatch(dashboardActions.onModalMode('constructionProgress'));
    };

    const contextValue: ProgressContextProps = {
        uploadMediaList,
        uploadedProgressMediaList,
        isUploadQueueStarted,
        uploadImagesList,
        onImageChange,
        appendPendingUploadMediaItems,
        clearPendingUploadMediaItems,
        getUploadMediaListSnapshot,
        clearUploadListImages,
        setUploadQueueStarted,
        setUploadMediaUploadingState,
        setUploadMediaError,
        setUploadMediaMeta,
        removeUploadMediaItems,
        setUploadAbortController,
        abortUploadMediaItem,
        addUploadedProgressMediaItems,
        clearUploadedProgressMediaList,
        isActiveMassSelectMode,
        activeMassSelectModeHandle,
        selectedMassMediaIds,
        onSelectedMassMediaIds,
        onALLSelectedMassMediaIds,
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        const uploadFiles = Array.from(e.target.files);
        onImageChange(uploadFiles);
        e.target.value = '';
    };

    const onNotOwnerHandler = useCallback(async () => {
        if (!is_owner) {
            setIsShowPopup(true);
        } else {
            // @ts-ignore
            const url = routable.getRoute('openChangeTariff');
            // @ts-ignore
            $.modalable('open', {
                url,
            });
        }
    }, []);

    const onHidePopup = useCallback(() => {
        setIsShowPopup(false);
    }, []);

    return {
        contextValue,
        handleInputChange,
        is_customer,
        progress_tariff_access_edit,
        onNotOwnerHandler,
        isShowPopup,
        onHidePopup,
        modalMode,
        t,
    };
};
